import { environment } from "src/environments/environment";
import { collection, doc, Firestore, getDoc, getDocs, limit, orderBy, query, where } from "@angular/fire/firestore";
import { Position } from "@capacitor/geolocation"
import { Injectable } from "@angular/core";
import { Auth } from "@angular/fire/auth";
import { firstValueFrom } from "rxjs";
import { IGeofencingConfig } from "@soposopo/sopo-models";
import { SopoPopupService } from "src/app/sopo-popup/service/sopo-popup.service";
import { SopoLoadingService } from "../sopo-loading/sopo-loading.service";
import { SopoTreeButtonConfirmSettings } from "src/app/sopo-popup/interfaces/tree-button-confirm";
import GeolocationSopoPlugin from "src/app/plugins/geolocation.plugin";
import { NativeGeocoder, Address } from '@capgo/nativegeocoder';
import { isElectron } from "src/app/shared/utils/platform.utils";
import { DesktopLocationQrComponent } from "src/app/shared/components/desktop-location-qr/desktop-location-qr.component";
import { SopoCheatService } from "src/app/sopo-cheat.service";

@Injectable({
    providedIn: "root",
})
export class GeolocationService {
    private _sessionGeoLocation: IGeolocationResult = null;

    constructor(
        private sopoPopupService: SopoPopupService,
        private loadingCtrl: SopoLoadingService,
        private firestore: Firestore,
        private auth: Auth,
        private sopoCheatService: SopoCheatService,
    ) { }

    public async isAllowedLocation(): Promise<boolean> {
        if (await this.sopoCheatService.checkGeofencingOverride()) return true;

        if (environment.staging) {
            const sopoTreeButtonConfirmSettings: SopoTreeButtonConfirmSettings = {
                data: {
                    header: 'Geoblock test',
                    allowText: 'Allow',
                    denyText: 'Deny',
                    continueText: 'Localize'
                },
                zIndex: 2002,
            }
            const popupWasChecked = localStorage.getItem(`dontShowAgain_locationPopup`);
            if (popupWasChecked === 'true') return true;
            const answer = await this.sopoPopupService.promptForTreeButtonConfirm('app', sopoTreeButtonConfirmSettings).then();
            if (answer === 'allow') return true;
            if (answer === 'deny') return false;
        }
        if (isElectron()) return this.handleDesktopLocation();
        this.loadingCtrl.resume();
        this.loadingCtrl.present('gps');

        try {
            const { whitelistArea, restrictedRegions, allowedCountries } = await this.getRestrictionConfig();
            const location = await this.getGeoLocation();

            this.loadingCtrl.dismiss('gps');

            if (this.insideArea(whitelistArea, [location.latitude, location.longitude])) return true;

            const isCountryCodeAllowed = allowedCountries.findIndex(country => country.countryCode.toLowerCase() === location.countryCode.toLowerCase()) >= 0;
            const isCountryNameAllowed = allowedCountries.findIndex(country => country.countryName.toLowerCase() === location.country.toLowerCase()) >= 0;
            if (!(isCountryCodeAllowed || isCountryNameAllowed)) {
                return false;
            }
            if (restrictedRegions.includes(location.administrativeArea)) return false;

            return true;
        } catch (error) {
            this.loadingCtrl.dismiss('gps');
            throw error;
        }
    }

    private async getRestrictionConfig(): Promise<IGeofencingConfig> {
        const restrictedDoc = await getDoc(doc(this.firestore, 'config/geofencing'));

        const geofencingConfig = restrictedDoc.data() as IGeofencingConfig | undefined;

        return {
            allowedCountries: geofencingConfig?.allowedCountries ?? [],
            restrictedRegions: geofencingConfig?.restrictedRegions ?? [],
            whitelistArea: geofencingConfig?.whitelistArea ?? [],
        };
    }

    private async getGeoLocation() {
        if (this._sessionGeoLocation) {
            return this._sessionGeoLocation;
        }

        const coordinates = await GeolocationSopoPlugin.getLocation();

        const address = await this.getAddress(coordinates);

        const result: IGeolocationResult = {
            latitude: coordinates.coords.latitude,
            longitude: coordinates.coords.longitude,
            countryCode: address.countryCode,
            country: address.countryName,
            administrativeArea: address.administrativeArea,
            accuracy: coordinates.coords.accuracy,
        };

        this._sessionGeoLocation = result;
        return result;
    }

    private async getAddress(coordinates: Position): Promise<Address> {
        const geocodeApiKey = environment.staging ? environment.mapsApiKey : undefined;

        const location = await NativeGeocoder.reverseGeocode({
            longitude: coordinates.coords.longitude,
            latitude: coordinates.coords.latitude,
            apiKey: geocodeApiKey
        });

        if (environment.staging) location.addresses.forEach(addr => console.log(addr));

        const address = location.addresses[0];

        return address;
    }

    private insideArea(vs: [number, number][], point: [number, number]) {
        var x = point[0], y = point[1];

        var inside = false;
        for (var i = 0, j = vs.length - 1; i < vs.length; j = i++) {
            var xi = vs[i][0], yi = vs[i][1];
            var xj = vs[j][0], yj = vs[j][1];

            var intersect = ((yi > y) != (yj > y))
                && (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
            if (intersect) inside = !inside;
        }

        return inside;
    }

    public async getUserCountryCode() {
        const location = await this.getGeoLocation();
        return location.countryCode.toLowerCase();
    }

    private async handleDesktopLocation(): Promise<boolean> {
        const result = await this.checkDesktopLocationFromAccessLogs();
        if (result !== null) return result;
        return this.showDesktopLocationQrDialog();
    }

    private async checkDesktopLocationFromAccessLogs(): Promise<boolean | null> {
        const userId = this.auth.currentUser?.uid;
        if (!userId) throw new Error('Not authenticated. Please log in.');

        const accessLogsRef = collection(this.firestore, `users/${userId}/accessLogs`);
        const q = query(accessLogsRef, where('approved', 'in', [true, false]), orderBy('accessTime', 'desc'), limit(1));
        const snapshot = await getDocs(q);

        if (snapshot.empty) return null;

        const latestLog = snapshot.docs[0].data();
        const twentyFourHoursMs = 24 * 60 * 60 * 1000;
        const accessTimeMs = latestLog.accessTime?.toMillis ? latestLog.accessTime.toMillis() : latestLog.accessTime;

        if (Date.now() - accessTimeMs > twentyFourHoursMs) return null;

        return latestLog.approved === true;
    }

    private async showDesktopLocationQrDialog(): Promise<boolean> {
        const dialogRef = this.sopoPopupService.openDialog('app', DesktopLocationQrComponent, {
            closeButton: true,
            zIndex: 2001,
            backdropDismiss: false,
        });

        if (!dialogRef) return false;

        const result = await firstValueFrom(dialogRef.afterClosed);
        return result === true;
    }
}

interface IGeolocationResult {
    latitude: number;
    longitude: number;
    countryCode: string;
    country: string;
    administrativeArea: string;
    accuracy: number;
}
